import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  ChevronDown,
  Crosshair,
  Dumbbell,
  Eye,
  Gauge,
  MessageSquareText,
  Save,
  Sparkles,
  Target,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ClubLogo } from './ClubLogo';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import type { Match, MatchContribution, Player } from './types';

type DevelopmentDomain = 'technical' | 'tactical' | 'physical' | 'mental';

type DevelopmentSignal = {
  playerId: string;
  weakness: string;
  domain: DevelopmentDomain;
  severity: number;
  note: string;
  matchId: string;
  updatedAt: string;
};

const WEAKNESSES: Array<{
  id: string;
  domain: DevelopmentDomain;
  label: string;
}> = [
  { id: 'first_touch', domain: 'technical', label: 'First touch' },
  { id: 'receiving_orientation', domain: 'technical', label: 'Receiving orientation' },
  { id: 'short_passing', domain: 'technical', label: 'Short passing' },
  { id: 'long_passing', domain: 'technical', label: 'Long passing' },
  { id: 'weak_foot', domain: 'technical', label: 'Weak foot' },
  { id: 'crossing', domain: 'technical', label: 'Crossing' },
  { id: 'finishing', domain: 'technical', label: 'Finishing' },
  { id: 'heading', domain: 'technical', label: 'Heading' },
  { id: 'dribbling', domain: 'technical', label: 'Ball carrying / dribbling' },
  { id: 'one_v_one', domain: 'technical', label: '1v1 attacking' },

  { id: 'scanning', domain: 'tactical', label: 'Scanning / awareness' },
  { id: 'decision_making', domain: 'tactical', label: 'Decision making' },
  { id: 'off_ball', domain: 'tactical', label: 'Off-ball movement' },
  { id: 'positioning', domain: 'tactical', label: 'Positioning' },
  { id: 'pressing_timing', domain: 'tactical', label: 'Pressing timing' },
  { id: 'defensive_positioning', domain: 'tactical', label: 'Defensive positioning' },
  { id: 'transition_reaction', domain: 'tactical', label: 'Transition reaction' },
  { id: 'build_up', domain: 'tactical', label: 'Build-up behavior' },
  { id: 'final_third', domain: 'tactical', label: 'Final-third decisions' },

  { id: 'acceleration', domain: 'physical', label: 'Acceleration' },
  { id: 'max_speed', domain: 'physical', label: 'Max speed' },
  { id: 'repeated_sprint', domain: 'physical', label: 'Repeated-sprint capacity' },
  { id: 'aerobic', domain: 'physical', label: 'Aerobic capacity' },
  { id: 'change_direction', domain: 'physical', label: 'Change of direction' },
  { id: 'power', domain: 'physical', label: 'Explosive power' },
  { id: 'strength', domain: 'physical', label: 'Strength' },
  { id: 'robustness', domain: 'physical', label: 'Robustness / availability' },

  { id: 'concentration', domain: 'mental', label: 'Concentration' },
  { id: 'communication', domain: 'mental', label: 'Communication' },
  { id: 'leadership', domain: 'mental', label: 'Leadership' },
  { id: 'composure', domain: 'mental', label: 'Composure' },
  { id: 'intensity', domain: 'mental', label: 'Intensity / competitiveness' },
];

function storageKey(teamId: string, playerId: string) {
  return `ecc_development_signal_${teamId}_${playerId}`;
}

function readSignals(teamId: string, playerId: string) {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(teamId, playerId)) || '[]');
    return Array.isArray(parsed) ? parsed as DevelopmentSignal[] : [];
  } catch {
    return [];
  }
}

function playerMatchContribution(match: Match, playerId: string) {
  return (match.contributions || []).find((item) => item.player_id === playerId) || null;
}

function ratingBand(rating: number | null | undefined) {
  const value = Number(rating ?? 0);
  if (value >= 8) return 'elite';
  if (value >= 7) return 'good';
  if (value >= 6) return 'average';
  return 'attention';
}

export function PostMatchReview() {
  const { t } = useI18n();
  const { team, players, matches, loading, error } = useData();

  const completedMatches = useMemo(
    () =>
      [...matches]
        .filter((match) => (
          match.status === 'completed' ||
          match.status === 'full_time' ||
          match.goals_for !== null ||
          match.goals_against !== null
        ))
        .sort((a, b) => b.match_date.localeCompare(a.match_date)),
    [matches],
  );

  const [selectedMatchId, setSelectedMatchId] = useState('');
  const selectedMatch =
    completedMatches.find((match) => match.id === selectedMatchId) ||
    completedMatches[0] ||
    null;

  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const selectedPlayer =
    players.find((player) => player.id === selectedPlayerId) ||
    null;

  const [signals, setSignals] = useState<DevelopmentSignal[]>([]);
  const [weakness, setWeakness] = useState('first_touch');
  const [severity, setSeverity] = useState(3);
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!selectedMatchId && completedMatches[0]) setSelectedMatchId(completedMatches[0].id);
  }, [completedMatches, selectedMatchId]);

  useEffect(() => {
    if (!selectedPlayer && players[0]) setSelectedPlayerId(players[0].id);
  }, [players, selectedPlayer]);

  useEffect(() => {
    if (!team || !selectedPlayer) {
      setSignals([]);
      return;
    }
    setSignals(readSignals(team.id, selectedPlayer.id));
  }, [team, selectedPlayer]);

  const contributions = useMemo(() => {
    if (!selectedMatch) return [];
    return players
      .map((player) => ({
        player,
        contribution: playerMatchContribution(selectedMatch, player.id),
      }))
      .filter(({ contribution }) => contribution)
      .sort((a, b) => Number(b.contribution?.rating ?? 0) - Number(a.contribution?.rating ?? 0));
  }, [players, selectedMatch]);

  const selectedContribution: MatchContribution | null =
    selectedMatch && selectedPlayer
      ? playerMatchContribution(selectedMatch, selectedPlayer.id)
      : null;

  const domainCounts = useMemo(() => {
    const counts = { technical: 0, tactical: 0, physical: 0, mental: 0 };
    signals.forEach((signal) => { counts[signal.domain] += 1; });
    return counts;
  }, [signals]);

  function saveSignal() {
    if (!team || !selectedPlayer || !selectedMatch) return;
    const definition = WEAKNESSES.find((item) => item.id === weakness);
    if (!definition) return;

    const signal: DevelopmentSignal = {
      playerId: selectedPlayer.id,
      weakness,
      domain: definition.domain,
      severity,
      note: note.trim(),
      matchId: selectedMatch.id,
      updatedAt: new Date().toISOString(),
    };

    const next = [
      signal,
      ...signals.filter((item) => item.weakness !== weakness),
    ].slice(0, 16);

    setSignals(next);
    try {
      localStorage.setItem(storageKey(team.id, selectedPlayer.id), JSON.stringify(next));
      setMessage(t.developmentSignalSaved);
      setNote('');
    } catch {
      setMessage(t.savedOnDevice);
    }
  }

  function removeSignal(weaknessId: string) {
    if (!team || !selectedPlayer) return;
    const next = signals.filter((signal) => signal.weakness !== weaknessId);
    setSignals(next);
    try {
      localStorage.setItem(storageKey(team.id, selectedPlayer.id), JSON.stringify(next));
    } catch {
      // Local-only enhancement.
    }
  }

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="performance-review-page">
      <section className="metal-card performance-review-hero">
        <div>
          <p className="eyebrow">PERFORMANCE & DEVELOPMENT INTELLIGENCE</p>
          <h1>{t.performanceReview}</h1>
          <p className="muted">{t.performanceReviewSubtitle}</p>
        </div>

        <label className="review-match-selector">
          <span>{t.completed}</span>
          <select value={selectedMatch?.id || ''} onChange={(event) => setSelectedMatchId(event.target.value)}>
            {completedMatches.map((match) => (
              <option key={match.id} value={match.id}>
                {new Date(match.match_date).toLocaleDateString()} · {match.opponent || '—'} · {match.goals_for ?? '-'}:{match.goals_against ?? '-'}
              </option>
            ))}
          </select>
        </label>
      </section>

      {selectedMatch ? (
        <>
          <section className="metal-card review-scoreboard">
            <div>
              <ClubLogo club="Polonia Glasgow" className="review-club-logo" />
              <strong>Polonia Glasgow AFC</strong>
            </div>
            <div>
              <small>{selectedMatch.competition || '—'}</small>
              <strong>{selectedMatch.goals_for ?? '-'} : {selectedMatch.goals_against ?? '-'}</strong>
              <span>{new Date(selectedMatch.match_date).toLocaleDateString()}</span>
            </div>
            <div>
              <ClubLogo club={selectedMatch.opponent} className="review-club-logo" />
              <strong>{selectedMatch.opponent || '—'}</strong>
            </div>
          </section>

          <section className="review-content-grid">
            <article className="metal-card review-player-ratings">
              <div className="section-title-line">
                <div>
                  <p className="eyebrow">{t.playerPerformance}</p>
                  <h2>{t.matchRatings}</h2>
                </div>
                <Activity size={23} />
              </div>

              <div className="review-rating-list">
                {contributions.map(({ player, contribution }) => (
                  <button
                    key={player.id}
                    className={selectedPlayer?.id === player.id ? 'selected' : ''}
                    onClick={() => setSelectedPlayerId(player.id)}
                  >
                    <span className="review-player-avatar">
                      {player.photo_url
                        ? <img src={player.photo_url} alt="" />
                        : <ClubLogo club="Polonia Glasgow" className="review-placeholder-logo" />}
                    </span>
                    <span className="review-player-copy">
                      <strong>#{player.squad_number ?? '—'} {player.name}</strong>
                      <small>{player.position || '—'} · {contribution?.minutes ?? 0} min</small>
                    </span>
                    <span className={`review-rating ${ratingBand(contribution?.rating)}`}>
                      {Number(contribution?.rating ?? 0).toFixed(1)}
                    </span>
                  </button>
                ))}
                {!contributions.length && <p className="muted">{t.noData}</p>}
              </div>
            </article>

            <article className="metal-card development-player-sheet">
              {selectedPlayer ? (
                <>
                  <div className="development-player-header">
                    <span>
                      {selectedPlayer.photo_url
                        ? <img src={selectedPlayer.photo_url} alt="" />
                        : <ClubLogo club="Polonia Glasgow" className="development-player-logo" />}
                    </span>
                    <div>
                      <p className="eyebrow">{t.developmentProfile}</p>
                      <h2>{selectedPlayer.name}</h2>
                      <small>#{selectedPlayer.squad_number ?? '—'} · {selectedPlayer.position || '—'}</small>
                    </div>
                    <strong>{Number(selectedContribution?.rating ?? 0).toFixed(1)}</strong>
                  </div>

                  <div className="development-match-metrics">
                    <div><span>{t.minutes}</span><strong>{selectedContribution?.minutes ?? 0}</strong></div>
                    <div><span>{t.goals}</span><strong>{selectedContribution?.goals ?? 0}</strong></div>
                    <div><span>{t.assists}</span><strong>{selectedContribution?.assists ?? 0}</strong></div>
                    <div><span>{t.started}</span><strong>{selectedContribution?.started ? '✓' : '—'}</strong></div>
                  </div>

                  <div className="development-domain-strip">
                    <span><Target size={15} /> {t.technical}: {domainCounts.technical}</span>
                    <span><Eye size={15} /> {t.tactical}: {domainCounts.tactical}</span>
                    <span><Gauge size={15} /> {t.physical}: {domainCounts.physical}</span>
                    <span><Sparkles size={15} /> {t.mental}: {domainCounts.mental}</span>
                  </div>

                  <section className="development-signal-editor">
                    <div className="section-title-line">
                      <div>
                        <p className="eyebrow">{t.developmentNeed}</p>
                        <h3>{t.flagWeakness}</h3>
                      </div>
                      <AlertTriangle size={20} />
                    </div>

                    <div className="development-editor-grid">
                      <label>
                        <span>{t.weakness}</span>
                        <select value={weakness} onChange={(event) => setWeakness(event.target.value)}>
                          {(['technical', 'tactical', 'physical', 'mental'] as const).map((domain) => (
                            <optgroup label={t[domain]} key={domain}>
                              {WEAKNESSES.filter((item) => item.domain === domain).map((item) => (
                                <option key={item.id} value={item.id}>{item.label}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </label>

                      <label>
                        <span>{t.priority}</span>
                        <select value={severity} onChange={(event) => setSeverity(Number(event.target.value))}>
                          {[1, 2, 3, 4, 5].map((value) => (
                            <option key={value} value={value}>{value}/5</option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <label className="development-note">
                      <span>{t.coachNotes}</span>
                      <textarea
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        placeholder={t.developmentNotePlaceholder}
                      />
                    </label>

                    <button className="gold-button" onClick={saveSignal}>
                      <Save size={16} />
                      {t.saveDevelopmentNeed}
                    </button>
                    {message && <p className="form-message">{message}</p>}
                  </section>

                  <section className="development-priority-list">
                    <div className="section-title-line">
                      <div>
                        <p className="eyebrow">{t.developmentPriorities}</p>
                        <h3>{t.trainingQueue}</h3>
                      </div>
                      <Dumbbell size={21} />
                    </div>

                    {signals.map((signal) => {
                      const definition = WEAKNESSES.find((item) => item.id === signal.weakness);
                      return (
                        <div className="development-priority-row" key={signal.weakness}>
                          <span className={`development-domain ${signal.domain}`}>{t[signal.domain]}</span>
                          <div>
                            <strong>{definition?.label || signal.weakness}</strong>
                            <small>{signal.note || t.noCoachNotes}</small>
                          </div>
                          <b>{signal.severity}/5</b>
                          <button onClick={() => removeSignal(signal.weakness)} aria-label={t.close}>×</button>
                        </div>
                      );
                    })}

                    {!signals.length && (
                      <div className="development-empty">
                        <BadgeCheck size={22} />
                        <span>{t.noDevelopmentNeeds}</span>
                      </div>
                    )}

                    <p className="training-module-preview">
                      <Crosshair size={17} />
                      {t.trainingModulePreview}
                    </p>
                  </section>
                </>
              ) : (
                <p className="muted">{t.noData}</p>
              )}
            </article>
          </section>
        </>
      ) : (
        <section className="metal-card empty-panel">
          <MessageSquareText />
          <p>{t.noCompletedMatches}</p>
        </section>
      )}
    </div>
  );
}
